// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Button, Typography } from "@insite/mobius/index";
import LazyImage from "@insite/mobius/LazyImage/LazyImage";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React, { useState } from "react";
import { css } from "styled-components";

export type ListingCategoriesProps = MobiusStyledComponentProps<
    "div",
    {
        showImages?: boolean;
        items: Array<any>;
        brand?: any;
        usage?: string;
        doNotDisplayLink?: boolean;
        categoriesNumber?: number | undefined;
        viewMoreButtonAsText?: boolean;
        fullWidth?: boolean;
        view?: string | undefined;
    }
>;

export interface ListingCategoriesStyles {
    container?: GridContainerProps;
    itemRow?: GridItemProps;
    innerContainer?: GridContainerProps;
    itemImage?: GridItemProps;
    imageLink?: LinkPresentationProps;
    itemLinkNameWrapper?: GridItemProps;
    itemLinkName?: LinkProps;
    viewOtherItems?: GridItemProps;
    viewMoreButtonAsText?: InjectableCss;
}

export const ListingCategoriesStyles: ListingCategoriesStyles = {
    container: {
        gap: 22,
    },
    innerContainer: {
        gap: 0,
        css: css`
            width: 100%;
        `,
    },
    itemRow: {
        width: [6, 6, 4, 3, 2],
    },
    itemImage: {
        width: 12,
        align: "middle",
        css: css`
            width: 100%;
            justify-content: center;
            margin-bottom: 5px !important;
        `,
    },
    itemLinkNameWrapper: {
        width: 12,
    },
    itemLinkName: {
        typographyProps: {
            css: css`
                width: 100%;
                overflow-wrap: break-word;
                word-wrap: break-word;
                text-align: center;
                line-height: 20px;
                padding: 0 15px;
            `,
        },
        color: "text.main",
        css: css`
            width: 100%;

            &,
            &:focus {
                outline: 0;
            }
        `,
    },
    imageLink: {
        css: css`
            height: 177px;
            overflow: hidden;
            position: relative;

            > span {
                line-height: 0;
            }

            &,
            &:focus {
                outline: 0;
            }

            &:after {
                content: " ";
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 1;
                background: ${getColor("secondary.main")};
                opacity: 0;
                transition: all 0.2s linear;
            }

            &:hover,
            &:focus {
                &:after {
                    opacity: 0.2;
                }
            }
        `,
    },
    viewOtherItems: {
        width: 12,
        css: css`
            justify-content: center;
            margin-top: 15px !important;
        `,
    },
    viewMoreButtonAsText: {
        css: css`
            cursor: pointer;
            color: ${getColor("primary.main")};
            font-size: 16px;
            margin-left: 8px;
        `,
    },
};

const styles = ListingCategoriesStyles;

const ListingCategories: React.FC<ListingCategoriesProps> = props => {
    const {
        showImages,
        items,
        brand,
        usage,
        doNotDisplayLink,
        categoriesNumber,
        viewMoreButtonAsText,
        fullWidth,
        view,
    } = props;
    const [showOtherItems, setShowOtherItems] = useState(false);
    const isCategories = usage === "categories";

    const displayItems = (displayedItems: any, extended?: boolean) => {
        return displayedItems.map((item: any, i: any) => {
            let preparedItem: any;

            if (typeof item === "object") {
                preparedItem = {
                    id: isCategories ? item.categoryId : item.id,
                    name: isCategories ? item.categoryShortDescription : item.name,
                    url: isCategories ? item.productListPagePath : item.urlSegment,
                    imageUrl: isCategories ? item.featuredImagePath : item.logoSmallImagePath,
                    imageAltText: isCategories ? item.featuredImageAltText : item.featuredAltText,
                };
            } else {
                preparedItem = { name: item };
            }

            if (view === "inline") {
                return (
                    <StyledWrapper key={uniqueId()}>
                        {doNotDisplayLink ? (
                            <Typography>{preparedItem.name}</Typography>
                        ) : (
                            <Link href={item.path}>{preparedItem.name}</Link>
                        )}
                        {((i !== displayedItems.length - 1 && displayedItems.length !== 1) ||
                            (showOtherItems && !extended)) && <Typography>,&nbsp;</Typography>}
                    </StyledWrapper>
                );
            }

            return (
                <GridItem width={fullWidth ? 12 : [6, 6, 4, 3, 2]} key={uniqueId()}>
                    <GridContainer {...styles.innerContainer}>
                        {showImages && (
                            <GridItem {...styles.itemImage}>
                                <Link href={preparedItem.url} {...styles.imageLink}>
                                    <LazyImage
                                        src={preparedItem.imageUrl}
                                        altText={preparedItem.imageAltText}
                                        css={css`
                                            img,
                                            p {
                                                line-height: 16px;
                                            }
                                        `}
                                    />
                                </Link>
                            </GridItem>
                        )}
                        <GridItem {...styles.itemLinkNameWrapper}>
                            {doNotDisplayLink ? (
                                <Typography>{preparedItem.name}</Typography>
                            ) : (
                                <Link
                                    href={preparedItem.url}
                                    {...styles.itemLinkName}
                                    data-test-selector={
                                        isCategories ? `brandCategoriesCategoryLink_${preparedItem.id}` : ``
                                    }
                                >
                                    {preparedItem.name}
                                </Link>
                            )}
                        </GridItem>
                    </GridContainer>
                </GridItem>
            );
        });
    };

    if (!items || !items.length) {
        return null;
    }

    const viewButton = [
        translate("View More"),
        (brand && brand.name) || " ",
        translate(isCategories ? "Categories" : "Sub Brands"),
    ].join(" ");

    return view === "inline" ? (
        <>
            {displayItems(items.slice(0, categoriesNumber || 12))}
            {showOtherItems && displayItems(items.slice(categoriesNumber || 12), true)}
            {items.length > 5 && (
                <StyledWrapper
                    as="span"
                    {...styles.viewMoreButtonAsText}
                    onClick={() => setShowOtherItems(!showOtherItems)}
                >
                    {!showOtherItems ? `... ${translate("Show More")}` : ` ${translate("Show Less")}`}
                </StyledWrapper>
            )}
        </>
    ) : (
        <GridContainer {...styles.container}>
            {displayItems(items.slice(0, categoriesNumber || 12))}
            {!showOtherItems && items.length > (categoriesNumber || 12) && view !== "inline" && (
                <GridItem {...styles.viewOtherItems}>
                    {viewMoreButtonAsText ? (
                        <StyledWrapper
                            as="span"
                            {...styles.viewMoreButtonAsText}
                            onClick={() => {
                                setShowOtherItems(true);
                            }}
                        >
                            {viewButton}
                        </StyledWrapper>
                    ) : (
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowOtherItems(true);
                            }}
                        >
                            {viewButton}
                        </Button>
                    )}
                </GridItem>
            )}
            {showOtherItems && displayItems(items.slice(categoriesNumber || 12))}
        </GridContainer>
    );
};

export default ListingCategories;
